import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';
import { DEMO } from '../../lib/demo';
import { Loader2, RefreshCw, ExternalLink, AlertTriangle, ShieldCheck, Flame, Hand, Quote, X } from 'lucide-react';

const ERR_TYPES = {
  adieu_ignore: '🚪 Adieu ignoré', promesse_non_tenue: '🤝 Promesse non tenue',
  demande_ignoree: '🙋 Demande ignorée', vente_forcee: '💸 Vente forcée',
  reponse_lente: '🐌 Réponse trop lente', reponse_expediee: '⚡ Réponse expédiée',
  incoherence: '🎭 Incohérence entre chatteurs', autre: '❓ Autre',
};

const ETATS = {
  risque: { label: '🔴 Risque', cls: 'border-rose-500/40 bg-rose-500/[0.06]', badge: 'bg-rose-500/15 text-rose-300', Icon: Flame },
  attention: { label: '🟠 À surveiller', cls: 'border-amber-500/30 bg-amber-500/[0.04]', badge: 'bg-amber-500/15 text-amber-300', Icon: AlertTriangle },
  ok: { label: '🟢 Bien géré', cls: 'border-white/10 bg-white/[0.02]', badge: 'bg-emerald-500/15 text-emerald-300', Icon: ShieldCheck },
};

// DEMANDES DES BALEINES (Marlon 13/09) : lues automatiquement une fois par semaine (plafond IA de 30 $
// par mois, aucun bouton ; plus de fiche fan depuis le 15/09), et on n'affiche que ce qui est fiable — la
// demande, la phrase exacte du fan, la date, le nombre de fois, et un lien qui ouvre la conversation
// sur ce message. Pas de « réglé / pas réglé » deviné par l'IA.
const TYPES_DEMANDE = {
  contenu: '📷 Contenu', contenu_personnalise: '🎬 Contenu perso', appel_ou_visio: '📞 Appel / visio',
  rencontre: '📍 Rencontre', autre_contact: '📱 Autre appli / numéro', paiement: '💶 Paiement',
  probleme: "⚠️ Problème d'achat", autre: '🔹 Autre',
};

function DemandesBaleine({ w, etat, onVoir, indispo, onReessayer, chargement }) {
  const plafond = !!etat?.plafond_atteint;
  const [tout, setTout] = useState(false);
  const enCours = etat?.statut === 'en_cours';
  const liste = etat?.liste || [];
  const lue = !!etat?.lu_le;
  // Dates FIXES de la période lue (une lecture d'il y a 5 jours ne dit pas « les 14 derniers jours »).
  const periode = etat?.lu_le
    ? `du ${etat.debut} au ${etat.lu_le}${etat.tronque ? ' (600 derniers messages)' : ''}`
    : 'sur la période lue';
  // Relecture ratée : les demandes affichées sont celles de la lecture d'avant.
  const precedente = etat?.statut === 'echec' && lue;
  const visibles = tout ? liste : liste.slice(0, 5);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5" data-testid={`demandes-${w.conversation_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] font-semibold text-white flex items-center gap-1.5"><Hand size={13} className="text-amber-300" /> Demandes du fan</span>
        {lue && !enCours && (
          <span className="text-[11px] text-slate-400" data-testid={`demandes-periode-${w.conversation_id}`}>
            {precedente ? `lecture précédente (${etat.lu_le})` : liste.length ? `${liste.length} ${periode}` : periode}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {enCours ? (
            <span className="text-[11px] text-indigo-300 flex items-center gap-1" data-testid={`demandes-en-cours-${w.conversation_id}`}><Loader2 size={12} className="animate-spin" /> Lecture en cours…</span>
          ) : indispo && !etat ? (
            <button onClick={onReessayer} className="min-h-[30px] text-[11px] px-2.5 rounded-md bg-white/5 hover:bg-white/10 text-slate-200 flex items-center gap-1">
              <RefreshCw size={11} /> Réessayer
            </button>
          ) : lue ? (
            <span className={`text-[11px] ${plafond ? 'text-amber-300/90' : 'text-slate-500'}`} data-testid={`demandes-prochaine-${w.conversation_id}`}>
              {plafond ? 'budget IA du mois atteint : reprise le 1er du mois'
                : etat.prochaine_le ? `prochaine lecture à partir du ${etat.prochaine_le} s'il écrit`
                  : etat.a_ecrit_depuis ? 'prochaine lecture sous peu' : "prochaine lecture dès qu'il écrit"}
            </span>
          ) : null}
        </span>
      </div>
      {indispo && !etat && (
        <div className="text-[11px] text-slate-400 mt-1">Demandes indisponibles pour l'instant (problème de connexion) : rien n'est relancé, réessaie dans un moment.</div>
      )}
      {!indispo && !lue && !enCours && etat?.statut !== 'echec' && (
        <div className="text-[11px] text-slate-500 mt-1" data-testid={`demandes-pas-lues-${w.conversation_id}`}>
          {chargement ? 'Chargement…'
            : etat?.statut === 'rien_a_lire' ? "Rien à lire : il n'a rien écrit ces 14 derniers jours. La lecture se fera quand il écrira."
              : plafond ? 'Pas encore lues : budget IA du mois atteint, la lecture reprend le 1er du mois.'
                : 'Pas encore lues : elles sont lues toutes seules dans les prochains jours (14 derniers jours, puis une fois par semaine).'}
        </div>
      )}
      {etat?.statut === 'echec' && ((etat.erreur || '').includes('rien écrit')
        ? <div className="text-[11px] text-slate-400 mt-1">Rien à lire : {etat.erreur}.</div>
        : <div className="text-[11px] text-amber-300/90 mt-1">La dernière lecture n'a pas marché{etat.prochaine_le ? `, nouvel essai à partir du ${etat.prochaine_le}` : ', nouvel essai automatique'}.</div>)}
      {etat?.statut === 'pret' && !enCours && liste.length === 0 && (
        <div className="text-[12px] text-slate-400 mt-1.5">Aucune demande claire du fan {periode}.</div>
      )}
      {liste.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {visibles.map((d, i) => (
            <li key={`${d.message_id}-${i}`} className="rounded-md border border-white/5 bg-black/20 px-2.5 py-2" data-testid={`demande-${w.conversation_id}-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] px-1.5 py-px rounded border border-white/10 bg-white/5 text-slate-300 whitespace-nowrap">{TYPES_DEMANDE[d.type] || TYPES_DEMANDE.autre}</span>
                <span className="text-[12px] text-slate-100 font-medium">{d.demande}</span>
                {d.fois > 1 && <span className="text-[10px] px-1.5 py-px rounded bg-amber-500/15 text-amber-300 border border-amber-500/25 whitespace-nowrap" title="Nombre de fois où il l'a demandé sur la période lue">demandé {d.fois} fois</span>}
                <span className="ml-auto text-[11px] text-slate-500 whitespace-nowrap" title="Date de la première fois où il l'a demandé">{d.fois > 1 ? `1re demande le ${d.quand}` : d.quand}</span>
              </div>
              <div className="mt-1 flex items-start gap-2">
                <div className="text-[11px] text-slate-300 italic flex gap-1 min-w-0 flex-1"><Quote size={10} className="shrink-0 mt-0.5 text-slate-500" /><span className="break-words">« {d.citation} »</span></div>
                {d.message_id && (
                  <button data-testid={`demande-voir-${w.conversation_id}-${i}`} onClick={() => onVoir(w, d)}
                    className="shrink-0 min-h-[26px] px-2 rounded-md bg-white/5 hover:bg-white/10 text-[11px] text-indigo-300 flex items-center gap-1 whitespace-nowrap">
                    Voir le message <ExternalLink size={10} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {liste.length > 5 && (
        <button onClick={() => setTout(t => !t)} className="mt-1.5 text-[11px] text-indigo-300 hover:text-indigo-200">
          {tout ? 'Voir moins' : `Voir les ${liste.length - 5} autres`}
        </button>
      )}
      {liste.length > 0 && (
        <div className="text-[10px] text-slate-500 mt-1.5">Lues par l'IA : la phrase du fan fait foi. Pour savoir ce qui a été répondu, clique « Voir le message ».</div>
      )}
    </div>
  );
}

// Le message d'une demande avec ce qui a été dit juste avant et après, lu sur place (même pour
// un vieux message que la messagerie ne charge plus), + lien pour ouvrir toute la conversation.
// Rendue dans <body> (portail) : sinon elle passe sous le menu latéral sur un écran moyen.
function ContexteMessage({ ouvert, onFermer, navigate }) {
  const [etat, setEtat] = useState({ chargement: true, lignes: [], erreur: null });
  const cibleRef = useRef(null);
  const filRef = useRef(null);
  const appuiSurFond = useRef(false);
  useEffect(() => {
    if (!ouvert) return undefined;
    let annule = false;
    setEtat({ chargement: true, lignes: [], erreur: null });
    api.get(`/whales/${ouvert.w.conversation_id}/messages/${ouvert.d.message_id}/contexte`)
      .then(({ data }) => { if (!annule) setEtat({ chargement: false, lignes: data.lignes || [], erreur: null }); })
      .catch((e) => { if (!annule) setEtat({ chargement: false, lignes: [], erreur: apiError(e, 'Message introuvable') }); });
    return () => { annule = true; };
  }, [ouvert]);
  useEffect(() => { if (!etat.chargement) cibleRef.current?.scrollIntoView({ block: 'center' }); }, [etat]);
  // Le clavier va dans la fenêtre (Espace / Page suivante font défiler le fil, pas la page derrière).
  useEffect(() => { if (ouvert) filRef.current?.focus({ preventScroll: true }); }, [ouvert]);
  useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (e) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [ouvert, onFermer]);
  if (!ouvert) return null;
  const { w, d } = ouvert;
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="contexte-message"
      onMouseDown={(e) => { appuiSurFond.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (appuiSurFond.current && e.target === e.currentTarget) onFermer(); appuiSurFond.current = false; }}>
      <div role="dialog" aria-modal="true" aria-label={`Message de ${w.fan}`} className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl border border-white/10 bg-[#0F1014] shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-sm font-bold text-white truncate">{w.fan} — {d.demande}</div>
            <div className="text-[11px] text-slate-400">Le message surligné, avec ce qui a été dit avant et après</div>
          </div>
          <button onClick={onFermer} aria-label="Fermer" className="ml-auto h-8 w-8 shrink-0 rounded-md hover:bg-white/10 text-slate-300 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div ref={filRef} tabIndex={-1} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-1.5 outline-none">
          {etat.chargement && <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> chargement…</div>}
          {etat.erreur && <div className="text-rose-300 text-sm">{etat.erreur}</div>}
          {etat.lignes.map((l) => (
            <div key={l.id} ref={l.cible ? cibleRef : null} className={`flex ${l.fan ? 'justify-start' : 'justify-end'}`} data-testid={l.cible ? 'contexte-cible' : undefined}>
              <div className={`max-w-[85%] rounded-xl px-3 py-1.5 border ${l.cible ? 'border-amber-400/70 bg-amber-500/15 ring-2 ring-amber-400/30' : l.fan ? 'border-white/10 bg-white/[0.04]' : 'border-indigo-500/20 bg-indigo-500/10'}`}>
                <div className="text-[10px] text-slate-400 mb-0.5">{l.fan ? w.fan : (l.chatteur || 'Équipe')} · {l.quand}</div>
                {(l.media || l.lien_paiement) && (
                  <div className="text-[11px] text-slate-400 italic">{[l.media ? `📎 ${l.media}` : null, l.lien_paiement ? '🔗 lien de paiement' : null].filter(Boolean).join(' · ')}</div>
                )}
                {l.texte && <div className="text-[13px] text-slate-100 whitespace-pre-wrap break-words">{l.texte}</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
          <span className="text-[11px] text-slate-500">Cette fenêtre ne marque rien comme lu.</span>
          <button onClick={() => navigate(`/dashboard/messages?account=${w.account_id}&conv=${w.conversation_id}&msg=${d.message_id}`)}
            className="ml-auto min-h-[32px] px-3 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-[12px] font-medium flex items-center gap-1.5">
            Ouvrir la conversation <ExternalLink size={12} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function Whales() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!DEMO);
  const [refreshing, setRefreshing] = useState(false);
  const [modele, setModele] = useState('');
  const [days, setDays] = useState(14);
  const [errs, setErrs] = useState(null);
  const [errOpen, setErrOpen] = useState('');
  const [retirees, setRetirees] = useState(null);  // null = panneau replie
  const [tstats, setTstats] = useState(null);
  const [demandes, setDemandes] = useState({});      // conversation_id → demandes déjà lues
  const [budget, setBudget] = useState(null);         // dépenses IA du mois (demandes des baleines) face au plafond
  const [contexte, setContexte] = useState(null);     // { w, d } : message d'une demande ouvert sur place
  const [echecsSondage, setEchecsSondage] = useState(0);
  const [demandesOk, setDemandesOk] = useState(null);  // false = le chargement des demandes a raté
  const dataRef = useRef(null);
  const sequenceDemandes = useRef(0);

  const load = useCallback(async (fresh = false, d = days) => {
    if (fresh) { setRefreshing(true); toast.info('Analyse des baleines en cours… (~1-2 min, IA par fan)'); }
    else if (!dataRef.current) setLoading(true);  // déjà des cartes : on ne les démonte pas
    try {
      const { data } = await api.get('/whales/report', { params: { days: d, ...(fresh ? { fresh: 1 } : {}) }, timeout: 60000 });
      dataRef.current = data;
      setData(data);
      setLoading(false);
      // Le recalcul tourne en fond côté serveur → on re-polle jusqu'à la fin.
      if (data.computing) { setRefreshing(true); setTimeout(() => load(false, d), 20000); return; }
    } catch (e) { toast.error(apiError(e, 'Échec du chargement')); }
    setLoading(false); setRefreshing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!DEMO) {
      api.get('/whales/errors').then(({ data }) => setErrs(data)).catch(() => {});
      api.get('/whales/treatments/stats').then(({ data }) => setTstats(data)).catch(() => {});
    }
  }, []);

  // Retirer une erreur du compteur : elle ne compte plus pour le chatteur et ne
  // revient PAS au prochain recalcul IA (le serveur la marque au lieu de l'effacer).
  const retirerErreur = async (chatter, e) => {
    setErrs(d => ({
      ...d,
      nb_retirees: (d.nb_retirees || 0) + 1,
      chatters: (d.chatters || []).map(c => c.chatter !== chatter ? c : {
        ...c,
        total: c.total - 1,
        types: Object.fromEntries(Object.entries(c.types || {})
          .map(([t, n]) => [t, t === e.type ? n - 1 : n]).filter(([, n]) => n > 0)),
        erreurs: (c.erreurs || []).filter(x => x.id !== e.id),
      }).filter(c => c.total > 0),
    }));
    try {
      await api.post(`/whales/errors/${e.id}/retirer`);
      toast.success(`Erreur retirée du compteur de ${chatter}`);
      if (retirees !== null) chargerRetirees();
    } catch (err) {
      toast.error(apiError(err, 'Échec du retrait'));
      api.get('/whales/errors').then(({ data }) => setErrs(data)).catch(() => {});
    }
  };

  const chargerRetirees = async () => {
    try {
      const { data } = await api.get('/whales/errors/retirees');
      setRetirees(data.erreurs || []);
    } catch (e) { toast.error(apiError(e, 'Échec du chargement')); }
  };

  const remettreErreur = async (e) => {
    try {
      await api.delete(`/whales/errors/${e.id}/retirer`);
      toast.success(`Erreur remise pour ${e.chatter}`);
      const [r1, r2] = await Promise.all([
        api.get('/whales/errors'), api.get('/whales/errors/retirees')]);
      setErrs(r1.data); setRetirees(r2.data.erreurs || []);
    } catch (err) { toast.error(apiError(err, 'Échec')); }
  };

  const toggleTraite = async (w) => {
    try {
      if (w.traite_aujourdhui) await api.delete(`/whales/${w.conversation_id}/traite`);
      else await api.post(`/whales/${w.conversation_id}/traite`);
      // Mise à jour locale immédiate (le poll suivant confirmera).
      setData(d => ({
        ...d,
        whales: (d?.whales || []).map(x => x.conversation_id !== w.conversation_id ? x
          : { ...x, traite_aujourdhui: w.traite_aujourdhui ? null : { par: 'moi' } }),
      }));
    } catch (e) { toast.error(apiError(e, 'Échec')); }
  };

  const chargerDemandes = useCallback(async () => {
    const numero = ++sequenceDemandes.current;
    try {
      const { data: d } = await api.get('/whales/demandes');
      if (numero === sequenceDemandes.current) { setDemandes(d || {}); setEchecsSondage(0); setDemandesOk(true); }
    } catch {
      if (numero === sequenceDemandes.current) { setEchecsSondage(n => n + 1); setDemandesOk(ok => (ok ? ok : false)); }  // la page reste utilisable
    }
    api.get('/ia/budget').then(({ data: b }) => setBudget(b)).catch(() => {});
  }, []);
  useEffect(() => { chargerDemandes(); }, [chargerDemandes]);
  // Une lecture automatique en cours : on revient voir toutes les 10 s, et on continue même après une
  // coupure réseau (espacé jusqu'à 60 s), sinon « Lecture en cours… » resterait affiché pour toujours.
  useEffect(() => {
    if (!Object.values(demandes).some(x => x?.statut === 'en_cours')) return undefined;
    const t = setTimeout(chargerDemandes, echecsSondage ? Math.min(60000, 10000 * 2 ** echecsSondage) : 10000);
    return () => clearTimeout(t);
  }, [demandes, echecsSondage, chargerDemandes]);

  const fermerContexte = useCallback(() => setContexte(null), []);
  const whales = (data?.whales || []).filter(w => !modele || w.modele === modele);
  const modeles = Array.from(new Set((data?.whales || []).map(w => w.modele)));
  const nRisque = (data?.whales || []).filter(w => (w.analyse || {}).etat === 'risque').length;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto fade-in" data-testid="whales-page">
      <PageHeader title="Baleines" subtitle="Les plus gros payeurs, surveillés de près — promesses, demandes sans suite, signaux de départ" onRefresh={() => { load(); chargerDemandes(); }} />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-4 flex items-center gap-3 flex-wrap">
        <select value={modele} onChange={e => setModele(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 text-xs text-white h-9 outline-none">
          <option value="" className="bg-[#0B0B0E]">Tous les modèles</option>
          {modeles.map(m => <option key={m} value={m} className="bg-[#0B0B0E]">{m}</option>)}
        </select>
        <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 h-7 rounded-md text-xs font-medium transition-colors ${days === d ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{d} j</button>
          ))}
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs text-indigo-200 bg-indigo-500/10 border border-indigo-400/30 hover:bg-indigo-500/20 disabled:opacity-50">
          {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {refreshing ? 'Analyse…' : 'Ré-analyser maintenant'}
        </button>
        {nRisque > 0 && <span className="text-xs text-rose-300 font-semibold">🔴 {nRisque} baleine(s) en risque</span>}
        {budget && (
          <span data-testid="ia-budget" className={`text-[11px] ${budget.mois_atteint ? 'text-amber-300' : 'text-slate-400'}`}
            title="Ce que les demandes des baleines ont coûté ce mois-ci. Au plafond, tout s'arrête jusqu'au 1er du mois.">
            IA ce mois : {Number(budget.mois_usd || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{'\u00a0'}$ / {budget.plafond_mois_usd}{'\u00a0'}$
          </span>
        )}
        {budget?.baleines?.depuis && (
          <span data-testid="ia-baleines" className="text-[11px] text-slate-400"
            title="Vrai co\u00fbt de l'analyse IA des baleines (hors plafond des demandes). \u00ab Sans IA \u00bb = rien n'avait boug\u00e9 chez ce fan : l'analyse pr\u00e9c\u00e9dente est reprise sans payer.">
            Analyse des baleines : {Number(budget.baleines.mois_usd || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{'\u00a0'}$ depuis le {budget.baleines.depuis}{' \u00b7 '}{budget.baleines.analyses} faites, {budget.baleines.reprises} sans IA
          </span>
        )}
        {data?.computed_at && <span className="ml-auto text-[11px] text-slate-500">analysé {new Date(data.computed_at + 'Z').toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · 7 derniers jours</span>}
      </div>

      {tstats && (tstats.total > 0 || (tstats.revenus || []).length > 0) && (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-3 mb-4 flex items-center gap-x-4 gap-y-1 flex-wrap text-[12px] text-slate-300">
          <span className="font-bold text-white">✅ Problèmes traités</span>
          <span><b className="text-emerald-300">{tstats.semaine}</b> cette semaine</span>
          <span><b className="text-emerald-300">{tstats.mois}</b> ce mois</span>
          {(tstats.par_personne || []).map(([n, c]) => (
            <span key={n} className="px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-200">{n} ({c})</span>
          ))}
          {(tstats.revenus || []).length > 0 && (
            <span className="text-rose-300" title={(tstats.revenus || []).map(r => `${r.fan} (traité le ${r.dernier_traitement})`).join(' · ')}>
              ⟳ {(tstats.revenus || []).length} revenu{(tstats.revenus || []).length > 1 ? 's' : ''} après traitement
            </span>
          )}
        </div>
      )}

      {(errs?.chatters || []).length > 0 && (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.03] p-4 mb-4">
          <div className="text-sm font-bold text-white mb-1">🧮 Compteur d'erreurs par chatteur</div>
          <div className="text-[11px] text-slate-400 mb-3">Fautes détectées par l'analyse IA sur les baleines, cumulées dans le temps (une même erreur n'est jamais comptée deux fois). Clique un nom pour le détail.</div>
          <div className="space-y-2">
            {errs.chatters.map((c) => (
              <div key={c.chatter} className="rounded-xl border border-white/10 bg-white/[0.02]">
                <button onClick={() => setErrOpen(errOpen === c.chatter ? '' : c.chatter)}
                  className="w-full flex items-center gap-3 flex-wrap p-2.5 text-left">
                  <span className="text-sm font-bold text-white">{c.chatter}</span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${c.total >= 3 ? 'bg-rose-500/15 text-rose-300' : 'bg-amber-500/15 text-amber-300'}`}>{c.total} erreur{c.total > 1 ? 's' : ''}</span>
                  {Object.entries(c.types || {}).map(([t, n]) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300">{ERR_TYPES[t] || t}{n > 1 ? ` ×${n}` : ''}</span>
                  ))}
                  <span className="ml-auto text-[11px] text-slate-400">{(c.baleines || []).length} baleine{(c.baleines || []).length > 1 ? 's' : ''} touchée{(c.baleines || []).length > 1 ? 's' : ''} · <b className="text-rose-300">{(c.ca_en_jeu || 0).toLocaleString('fr-FR')} € en jeu</b></span>
                </button>
                {errOpen === c.chatter && (
                  <div className="px-3 pb-3 space-y-2">
                    {(c.erreurs || []).map((e, i) => (
                      <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
                        <div className="text-[11px] text-slate-400 mb-0.5">
                          <b className="text-slate-200">{e.fan}</b> ({e.modele}) · {ERR_TYPES[e.type] || e.type} · vu le {e.last_seen ? new Date(e.last_seen + 'Z').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}
                          <button onClick={() => navigate(`/dashboard/messages?account=${e.account_id}&conv=${e.conversation_id}`)}
                            className="ml-2 text-indigo-300 hover:text-indigo-200">ouvrir la conv</button>
                          <button onClick={() => retirerErreur(c.chatter, e)}
                            title="Ne pas compter cette erreur pour ce chatteur"
                            className="ml-2 px-1.5 py-0.5 rounded border border-white/15 text-slate-300 hover:text-white hover:border-white/30">
                            ✕ pas une erreur
                          </button>
                        </div>
                        <div className="text-[12px] text-slate-200">{e.description}</div>
                        {e.citation && <div className="text-[11px] text-slate-400 italic mt-0.5">« {e.citation} »</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {(errs?.nb_retirees || 0) > 0 && (
            <div className="mt-3 pt-2 border-t border-white/10">
              <button onClick={() => (retirees === null ? chargerRetirees() : setRetirees(null))}
                className="text-[11px] text-slate-400 hover:text-slate-200">
                {retirees === null ? '▸' : '▾'} {errs.nb_retirees} erreur{errs.nb_retirees > 1 ? 's' : ''} retirée{errs.nb_retirees > 1 ? 's' : ''} du compteur
              </button>
              {retirees !== null && (
                <div className="mt-2 space-y-1.5">
                  {retirees.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 flex-wrap rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
                      <span className="text-[11px] text-slate-300"><b>{e.chatter}</b> · {e.fan} · {ERR_TYPES[e.type] || e.type}</span>
                      {e.ignore_par && <span className="text-[10px] text-slate-500">retirée par {e.ignore_par}</span>}
                      <button onClick={() => remettreErreur(e)}
                        className="ml-auto text-[11px] text-indigo-300 hover:text-indigo-200">remettre</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <ContexteMessage ouvert={contexte} onFermer={fermerContexte} navigate={navigate} />
      {loading ? <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> chargement…</div>
        : whales.length === 0 ? <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-slate-400 text-sm">Aucune baleine analysée — clique « Ré-analyser maintenant ».</div>
          : (
            <div className="space-y-3">
              {whales.map((w) => {
                const A = w.analyse || {};
                const E = ETATS[A.etat] || ETATS.ok;
                return (
                  <div key={w.conversation_id} className={`rounded-2xl border ${E.cls} p-4`}>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${E.badge}`}>{E.label}</span>
                      {w.etat_precedent && w.etat_precedent !== (A.etat || 'ok') && (
                        <span className="text-[10px] text-slate-500">était : {w.etat_precedent}</span>
                      )}
                      {w.montante && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300">📈 Montante</span>}
                      <span className="text-sm font-bold text-white">{w.fan}</span>
                      <span className="text-[11px] text-slate-400">({w.modele})</span>
                      <span className="text-xs text-emerald-400 font-bold">{(w.spent || 0).toLocaleString('fr-FR')} € au total</span>
                      <span className="text-[11px] text-slate-400">· {(w.ca_periode || 0).toLocaleString('fr-FR')} € sur 7j · {w.nb_echanges} échanges</span>
                      {w.tendance_pct != null && (
                        <span className={`text-[11px] font-semibold ${w.tendance_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                          title="Dépenses des 2 dernières semaines vs les 2 d'avant">
                          {w.tendance_pct >= 0 ? '↗' : '↘'} {w.tendance_pct > 0 ? '+' : ''}{w.tendance_pct}% dépense
                        </span>
                      )}
                      {(w.depense_hebdo || []).some(v => v > 0) && (
                        <span className="flex items-end gap-[2px] h-4" title={`CA hebdo (6 sem.) : ${(w.depense_hebdo || []).join(' € / ')} €`}>
                          {(w.depense_hebdo || []).map((v, i) => {
                            const mx = Math.max(...w.depense_hebdo, 1);
                            return <span key={i} className={`w-1.5 rounded-sm ${i >= 4 ? 'bg-indigo-400' : 'bg-slate-600'}`} style={{ height: `${Math.max(2, (v / mx) * 16)}px` }} />;
                          })}
                        </span>
                      )}
                      {w.probleme_revenu && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200"
                          title="Cette baleine avait été marquée traitée un jour précédent, mais elle n'est pas repassée au vert.">⟳ Problème revenu</span>
                      )}
                      <button onClick={() => navigate(`/dashboard/messages?account=${w.account_id}&conv=${w.conversation_id}`)}
                        className="ml-auto text-[11px] text-indigo-300 hover:text-indigo-200 flex items-center gap-1">Ouvrir la conv <ExternalLink size={11} /></button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <button onClick={() => toggleTraite(w)}
                        title="Quand cocher ? Quand l'ACTION qui répond au problème a été faite — le message de rattrapage est parti au fan, ou le chatteur concerné a été briefé. Pas juste « je l'ai lu »."
                        className={`text-[11px] px-2 py-1 rounded-lg border flex items-center gap-1.5 ${w.traite_aujourdhui
                          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                          : 'bg-white/[0.04] text-slate-300 border-white/10 hover:bg-white/[0.08]'}`}>
                        {w.traite_aujourdhui
                          ? <>✅ Traité aujourd'hui{w.traite_aujourdhui.par && w.traite_aujourdhui.par !== 'moi' ? ` par ${w.traite_aujourdhui.par}` : ''} — cliquer pour annuler</>
                          : <>☐ Marquer « problème traité aujourd'hui »</>}
                      </button>
                      {(w.traitements || []).slice(w.traite_aujourdhui ? 1 : 0).length > 0 && (
                        <span className="text-[10px] text-slate-500">
                          déjà traité : {(w.traitements || []).slice(w.traite_aujourdhui ? 1 : 0).map(t => `${t.date?.slice(8, 10)}/${t.date?.slice(5, 7)} (${t.par})`).join(' · ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] text-slate-400 mb-1.5">
                      {w.derniere_vente && <span>🛒 Dernier achat : <b className={w.derniere_vente.il_y_a_jours > 10 ? 'text-rose-300' : 'text-slate-200'}>{w.derniere_vente.montant} € il y a {w.derniere_vente.il_y_a_jours} j</b></span>}
                      {w.nb_achats != null && <span>{w.nb_achats} achats · panier <b className="text-slate-200">{w.panier_moyen ?? '—'} €</b></span>}
                      {w.fan_depuis && <span>fan depuis {w.fan_depuis}</span>}
                      {w.attend_paye_le && <span className="text-amber-300">💰 paye le {w.attend_paye_le}</span>}
                      {w.chatteur_principal && <span>👤 chatteur principal : <b className="text-slate-200">{w.chatteur_principal}</b></span>}
                      {w.reponse_med_s != null && <span>⏱ on lui répond en ~{w.reponse_med_s < 60 ? w.reponse_med_s + ' s' : Math.round(w.reponse_med_s / 60) + ' min'}</span>}
                      {(w.msgs_fan != null) && <span>{w.msgs_fan} msgs de lui / {w.msgs_nous} de nous</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-400 mb-2">
                      <span>Chatteurs (période) :</span>
                      {(w.chatters || []).map(([n, c]) => <span key={n} className="px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-200">{n} ({c})</span>)}
                      {w.dernier_envoi && <span className="ml-2">Dernier envoi : <b className="text-slate-300">{w.dernier_envoi.par}</b> le {w.dernier_envoi.quand}</span>}
                      {w.sans_reponse_depuis_h != null && <span className="text-amber-300">⏳ fan sans réponse depuis {w.sans_reponse_depuis_h}h</span>}
                    </div>
                    <div className="mb-2 space-y-2">
                      <DemandesBaleine w={w} etat={demandes[w.conversation_id]} onVoir={(ww, dd) => setContexte({ w: ww, d: dd })}
                        indispo={demandesOk === false} onReessayer={chargerDemandes} chargement={demandesOk === null} />
                      {/* 15/09 (Marlon) : bloc « Fiche fan » retiré, les notes automatiques le remplacent */}
                    </div>
                    {A.resume && <div className="text-[13px] text-slate-200 mb-2">{A.resume}</div>}
                    {(A.problemes || []).length > 0 && (
                      <div className="mb-2">
                        <div className="text-[11px] font-semibold text-rose-300 mb-1">Problèmes repérés</div>
                        <ul className="space-y-1">{A.problemes.map((p, i) => <li key={i} className="text-[12px] text-slate-300 flex gap-2"><span className="text-rose-400">•</span><span>{p}</span></li>)}</ul>
                      </div>
                    )}
                    {(A.erreurs_chatteurs || []).length > 0 && (
                      <div className="mb-2 flex items-center gap-1.5 flex-wrap text-[11px]">
                        <span className="text-slate-400">Fautes attribuées :</span>
                        {A.erreurs_chatteurs.map((e, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-200 border border-rose-500/20" title={e.description}>
                            👤 <b>{e.chatteur}</b> — {ERR_TYPES[e.type] || e.type}
                          </span>
                        ))}
                      </div>
                    )}
                    {(A.promesses_en_attente || []).length > 0 && (
                      <div className="mb-2">
                        <div className="text-[11px] font-semibold text-amber-300 mb-1">Promesses / demandes en attente</div>
                        <ul className="space-y-1">{A.promesses_en_attente.map((p, i) => <li key={i} className="text-[12px] text-slate-300 flex gap-2"><span className="text-amber-400">•</span><span>{p}</span></li>)}</ul>
                      </div>
                    )}
                    {(A.a_faire || []).length > 0 && (
                      <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/[0.05] p-2.5">
                        <div className="text-[11px] font-semibold text-indigo-300 mb-1">🎯 À faire</div>
                        <ul className="space-y-1">{A.a_faire.map((p, i) => <li key={i} className="text-[12px] text-slate-200 flex gap-2"><span className="text-indigo-400 font-bold">{i + 1}.</span><span>{p}</span></li>)}</ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
    </div>
  );
}
